/**
 * tileMapHelpers.ts
 * Tile ID constants and helper functions for TileMapLoader.
 * Extracted to keep TileMapLoader.ts under the 300-line limit.
 *
 * @module tileMapHelpers
 */

import type { AABB } from '../types/entity';
import type { WeaponPickupKind } from '../types/level';
import type { TiledProperty, TiledTileLayer, TiledObjectGroup, TiledLayer } from '../rendering/TileMapTypes';

// ── Tile IDs from brief 03 ────────────────────────────────────────────────────

/** Tile IDs that represent solid full-height ground collision geometry. */
export const GROUND_TILE_IDS = new Set([
  1, 2, 3, 4, 5, 6, 7, 8, 9,   // ground/cliff tiles (1-indexed, firstgid=1)
]);

/** Tile IDs for floating platforms — thin collision (top surface only). */
export const PLATFORM_TILE_IDS = new Set([17, 18, 19, 20]);

// DECISION: Platform collision is 6px tall at the top of the tile cell. This
// matches the visible sprite (shifted up 2px in the tileset) and lets Nova
// walk freely beneath platforms without hitting an invisible wall.
export const PLATFORM_COLLISION_HEIGHT = 6;

/** Tile IDs that represent upward-pointing spike hazards. */
export const SPIKE_UP_IDS = new Set([65]);   // tile 64 in 0-based = 65 in 1-based (firstgid=1)
/** Tile IDs that represent downward-pointing spike hazards. */
export const SPIKE_DOWN_IDS = new Set([66]);

/** Star seed tile ID (1-based, firstgid=1). */
export const STAR_SEED_ID = 49;
/** Constellation shard tile ID (1-based). */
export const CONSTELLATION_SHARD_ID = 50;
/** Heart fragment tile ID (1-based). */
export const HEART_FRAGMENT_ID = 51;
/** Inactive checkpoint tile ID (R3C6 checkpoint flag top). */
export const CHECKPOINT_INACTIVE_ID = 38;

/**
 * Weapon pickup tile IDs → variant kind, parsed from the Collectibles layer.
 *
 * DECISION: Map (not Set) so the parser does a single lookup per Collectibles
 * tile and routes straight to `WeaponPickupData.kind` without a per-kind branch.
 * Adjacent to the heart fragment ID (51) so all weapon-related collectible
 * tiles cluster in the 49–55 range — the Tiled tileset for World 3+ uses this
 * block for the same visual family.
 */
export const WEAPON_PICKUP_TILE_IDS: ReadonlyMap<number, WeaponPickupKind> = new Map([
  [52, 'gem_shard'],
  [53, 'boomerang'],
  [54, 'fire_orb'],
  [55, 'ice_crystal'],
]);

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Read a typed property from a Tiled property array.
 * @param properties - The Tiled property array (may be undefined).
 * @param name       - Property name to find.
 * @param fallback   - Default value if not found.
 */
export function getProp<T>(
  properties: TiledProperty[] | undefined,
  name: string,
  fallback: T,
): T {
  if (!properties) return fallback;
  const p = properties.find(prop => prop.name === name);
  return p ? (p.value as T) : fallback;
}

/** Type guard for TiledTileLayer. */
export function isTileLayer(l: TiledLayer): l is TiledTileLayer {
  return l.type === 'tilelayer';
}

/** Type guard for TiledObjectGroup. */
export function isObjectGroup(l: TiledLayer): l is TiledObjectGroup {
  return l.type === 'objectgroup';
}

/**
 * Merge adjacent tiles in the same row into horizontal AABB strips.
 * This reduces the platform count from O(tiles) to O(runs) which is
 * roughly 10× fewer AABBs for typical ground sections.
 */
export function extractHorizontalRuns(
  data: number[],
  mapWidth: number,
  mapHeight: number,
  tileW: number,
  tileH: number,
  matchIds: Set<number>,
): AABB[] {
  const result: AABB[] = [];
  for (let row = 0; row < mapHeight; row++) {
    let runStart = -1;
    let runLen = 0;
    for (let col = 0; col <= mapWidth; col++) {
      const idx = row * mapWidth + col;
      const tileId = col < mapWidth ? data[idx] : 0;
      const isSolid = matchIds.has(tileId);
      if (isSolid) {
        if (runStart === -1) {
          runStart = col;
          runLen = 1;
        } else {
          runLen++;
        }
      } else {
        if (runStart !== -1) {
          result.push({
            x: runStart * tileW,
            y: row * tileH,
            width: runLen * tileW,
            height: tileH,
          });
          runStart = -1;
          runLen = 0;
        }
      }
    }
  }
  return result;
}
